import { IPassengerForStationDto, RidesApi, SimpleResult } from "@ryde/shared";

/**
 * The passengers-with-stations request while it is in flight, shared across every hook instance.
 *
 * `useRideStationActions` guards its fetch on "the atom is already populated", but that guard cannot
 * prevent a duplicate on its own: it reads Recoil state captured in a per-instance `useEvent`
 * closure, so two components mounting in the same commit both see the atom empty and both POST - and
 * this is the heaviest reference payload in the app. Module scope rather than a ref is the whole
 * point, since the problem is precisely that hook instances do not share state. Mirrors the
 * `promiseCache` that `getDropdownItems` already keeps for the same reason.
 */
let inFlightRequest:
  Promise<SimpleResult<IPassengerForStationDto[]> | undefined> | undefined;

/**
 * Fetch the passengers-with-stations dropdown list, joining the request already in flight instead of
 * starting a second one. `force` always starts a fresh request.
 *
 * The entry is released as soon as the request settles - including on failure, so a failed attempt
 * can be retried rather than being remembered as "still in flight" forever - and only ever by the
 * caller that created it, so a concurrent forced refetch is never dropped.
 */
export const fetchPassengersWithStationsDeduped = async (
  force = false,
): Promise<SimpleResult<IPassengerForStationDto[]> | undefined> => {
  if (force) {
    inFlightRequest = undefined;
  }

  const request =
    inFlightRequest ?? RidesApi.fetchPassForDropdownWithStations();
  inFlightRequest = request;

  try {
    return await request;
  } finally {
    if (inFlightRequest === request) {
      inFlightRequest = undefined;
    }
  }
};
